import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { WarningCircle, Paperclip, X, CheckCircle } from '@phosphor-icons/react';

const REQUEST_TYPES = [
  { value: 'refabrication_identique', label: 'Refabrication identique' },
  { value: 'refabrication_modifiee', label: 'Refabrication modifiée' },
  { value: 'retouche', label: 'Retouche' },
  { value: 'casse', label: 'Casse' },
  { value: 'usure', label: 'Usure' },
  { value: 'inconfort', label: 'Inconfort' },
  { value: 'mauvaise_tenue', label: 'Mauvaise tenue' },
  { value: 'probleme_acoustique', label: 'Problème acoustique' },
  { value: 'defaut', label: 'Défaut de fabrication' },
  { value: 'autre', label: 'Autre' },
];

export default function SavRequestForm({ open, onOpenChange, order, onSuccess }) {
  const [form, setForm] = useState({
    request_type: '',
    description: '',
    problem_date: new Date().toISOString().split('T')[0],
    wants_return: false,
    urgency: 'normal',
    ear_side: '',
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Reset form when order changes or dialog opens
  useEffect(() => {
    if (open && order) {
      const defaultSide = order.ear_side === 'both' ? '' : (order.ear_side || '');
      setForm({
        request_type: '',
        description: '',
        problem_date: new Date().toISOString().split('T')[0],
        wants_return: false,
        urgency: 'normal',
        ear_side: defaultSide,
      });
      setFiles([]);
      setSuccess(null);
    }
  }, [open, order]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Resolve display label for ear side
  const getEarLabel = (side) => {
    if (side === 'left') return 'Embout gauche (L)';
    if (side === 'right') return 'Embout droit (R)';
    if (side === 'both') return 'Bilateral (L + R)';
    return '';
  };

  // Build ear side options based on order
  const earOptions = [];
  if (order) {
    if (order.ear_side === 'both') {
      earOptions.push({ value: 'left', label: `Embout gauche (L)${order.serial_number_left ? ` — ${order.serial_number_left}` : ''}` });
      earOptions.push({ value: 'right', label: `Embout droit (R)${order.serial_number_right ? ` — ${order.serial_number_right}` : ''}` });
      earOptions.push({ value: 'both', label: 'Bilateral (L + R)' });
    } else if (order.ear_side === 'left') {
      earOptions.push({ value: 'left', label: `Embout gauche (L)${order.serial_number_left ? ` — ${order.serial_number_left}` : ''}` });
    } else if (order.ear_side === 'right') {
      earOptions.push({ value: 'right', label: `Embout droit (R)${order.serial_number_right ? ` — ${order.serial_number_right}` : ''}` });
    }
  }

  const handleSubmit = async () => {
    if (!form.request_type) { toast.error('Veuillez sélectionner un type de demande'); return; }
    if (!form.ear_side) { toast.error('Veuillez sélectionner la partie concernée'); return; }
    if (!form.description.trim()) { toast.error('Veuillez décrire le problème'); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/sav`, {
        order_id: order.id,
        request_type: form.request_type,
        ear_side: form.ear_side,
        description: form.description,
        problem_date: form.problem_date,
        wants_return: form.wants_return,
        urgency: form.urgency,
        suspected_nonconformity: !!form.suspected_nonconformity,
      });

      const savId = res.data.id;

      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          await axios.post(`${API}/sav/${savId}/attachments`, fd);
        } catch (e) { console.error('Attachment upload error:', e.message); }
      }

      setSuccess(res.data.sav_number);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[95vw] sm:max-w-lg" data-testid="sav-request-dialog">
        {success ? (
          <>
            <div className="flex flex-col items-center py-8 text-center" data-testid="sav-success-message">
              <CheckCircle size={48} weight="fill" className="text-emerald-500 mb-4" />
              <h3 className="font-heading font-bold text-lg mb-2">Demande SAV envoyée</h3>
              <p className="text-sm text-zinc-600 mb-1">Votre demande <strong>{success}</strong> a bien été enregistrée.</p>
              <p className="text-sm text-zinc-500">Vous serez informé par email de l'avancement.</p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 w-full sm:w-auto" data-testid="sav-close-btn">
                Fermer
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Demande SAV</DialogTitle>
              <DialogDescription>
                Commande : {order.patient_reference} — {order.product_name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Éligibilité commerciale (séparée du traitement qualité, toujours possible) */}
              {(() => {
                const ref = order.completed_at || order.created_at;
                const exp = ref ? new Date(new Date(ref).getTime() + 365 * 86400000) : null;
                const eligible = exp ? exp >= new Date() : null;
                return (
                  <div className={`p-2.5 rounded-sm border text-xs ${eligible === false ? 'bg-zinc-50 border-zinc-200 text-zinc-700' : 'bg-blue-50 border-blue-200 text-blue-900'}`} data-testid="sav-eligibility">
                    {eligible === false
                      ? <>Garantie commerciale (12 mois) <strong>expirée</strong> le {exp.toLocaleDateString('fr-FR')} — la réclamation reste enregistrable et sera traitée au titre de la qualité ; une refabrication pourra être facturée.</>
                      : eligible === true ? <>Garantie commerciale 12 mois : <strong>éligible</strong> jusqu'au {exp.toLocaleDateString('fr-FR')}.</> : 'Éligibilité commerciale : date de référence indisponible.'}
                  </div>
                );
              })()}

              {/* Info refabrication identique gratuite */}
              {form.request_type === 'refabrication_identique' && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-sm flex items-start gap-2" data-testid="sav-gratuit-info">
                  <WarningCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-emerald-800">Toute refabrication à l'identique est <strong>gratuite</strong>, sans exception.</p>
                </div>
              )}

              {/* Type de demande */}
              <div>
                <Label className="label-overline mb-2 block">Type de demande *</Label>
                <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="sav-request-type"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {REQUEST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Partie concernée */}
              <div>
                <Label className="label-overline mb-2 block">Partie concernée *</Label>
                {earOptions.length === 1 ? (
                  <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm text-sm" data-testid="sav-ear-auto">
                    {earOptions[0].label}
                  </div>
                ) : (
                  <Select value={form.ear_side} onValueChange={(v) => setForm({ ...form, ear_side: v })}>
                    <SelectTrigger className="rounded-sm bg-white" data-testid="sav-ear-side"><SelectValue placeholder="Selectionner la partie..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {earOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Description */}
              <div>
                <Label className="label-overline mb-2 block">Description du problème *</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Décrivez le problème rencontré..."
                  className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-zinc-900 text-sm"
                  data-testid="sav-description"
                />
              </div>

              {/* Date du probleme */}
              <div>
                <Label className="label-overline mb-2 block">Date du problème</Label>
                <Input
                  type="date"
                  value={form.problem_date}
                  onChange={(e) => setForm({ ...form, problem_date: e.target.value })}
                  className="rounded-sm"
                  data-testid="sav-problem-date"
                />
              </div>

              {/* Retour produit + Urgence */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="label-overline mb-2 block">Retourner le produit ?</Label>
                  <RadioGroup value={form.wants_return ? 'oui' : 'non'} onValueChange={(v) => setForm({ ...form, wants_return: v === 'oui' })} className="flex gap-4">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="oui" id="retour-oui" data-testid="sav-return-yes" /><Label htmlFor="retour-oui" className="cursor-pointer text-sm">Oui</Label></div>
                    <div className="flex items-center space-x-2"><RadioGroupItem value="non" id="retour-non" data-testid="sav-return-no" /><Label htmlFor="retour-non" className="cursor-pointer text-sm">Non</Label></div>
                  </RadioGroup>
                </div>
                <div>
                  <Label className="label-overline mb-2 block">Urgence</Label>
                  <RadioGroup value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })} className="flex gap-4">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="normal" id="urgence-normal" data-testid="sav-urgency-normal" /><Label htmlFor="urgence-normal" className="cursor-pointer text-sm">Normal</Label></div>
                    <div className="flex items-center space-x-2"><RadioGroupItem value="urgent" id="urgence-urgent" data-testid="sav-urgency-urgent" /><Label htmlFor="urgence-urgent" className="cursor-pointer text-sm">Urgent</Label></div>
                  </RadioGroup>
                </div>
              </div>

              {/* Suspicion de non-conformité du dispositif → traitement qualité / réglementaire (pas une déclaration automatique) */}
              <label className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${form.suspected_nonconformity ? 'border-red-300 bg-red-50' : 'border-zinc-200 bg-white'}`} data-testid="sav-suspected-label">
                <input type="checkbox" checked={!!form.suspected_nonconformity} onChange={(e) => setForm({ ...form, suspected_nonconformity: e.target.checked })} className="mt-1 h-4 w-4 accent-red-600" data-testid="sav-suspected-checkbox" />
                <span className="text-sm">
                  <span className="font-medium">Suspicion de non-conformité du dispositif</span>
                  <span className="block text-xs text-zinc-500 mt-0.5">Le dossier sera identifié dans la liste de traitement qualité / réglementaire de HAMAC. Il ne s'agit pas d'une déclaration automatique aux autorités.</span>
                </span>
              </label>

              {/* Pieces jointes */}
              <div>
                <Label className="label-overline mb-2 block">Pièces jointes</Label>
                <label className="flex items-center gap-2 p-3 border border-dashed border-zinc-300 rounded-sm cursor-pointer hover:border-zinc-500 transition-colors">
                  <Paperclip size={16} className="text-zinc-400" />
                  <span className="text-sm text-zinc-500">Ajouter photos, videos ou documents</span>
                  <input type="file" multiple className="hidden" onChange={handleFileChange} accept="image/*,video/*,.pdf,.doc,.docx" data-testid="sav-file-input" />
                </label>
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <div key={`${f.name}-${f.size}`} className="flex items-center gap-2 text-sm bg-zinc-50 px-2 py-1 rounded-sm">
                        <Paperclip size={12} className="text-zinc-400" />
                        <span className="truncate flex-1">{f.name}</span>
                        <button onClick={() => removeFile(i)} className="text-zinc-400 hover:text-rose-500"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm w-full sm:w-auto">Annuler</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 w-full sm:w-auto" data-testid="sav-submit-btn">
                {submitting ? 'Envoi...' : 'Envoyer la demande'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
